import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Typography, Button, TextField, Alert, IconButton, CircularProgress, Box, FormControlLabel, Switch } from '@mui/material';
import { ContentCopy, CheckCircle, Visibility, VisibilityOff, Refresh } from '@mui/icons-material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { ManagementActions } from '../../../Services/_actions';
import LoadingButton from '../../Shared/Loading/index.tsx';

const CreateApiKey: React.FC = () => {
  const [resData, setResData] = useState < { is_active: boolean, api_key: string } | null > (null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCopyIcon, setShowCopyIcon] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const { showSnackBar } = useSnackBar();
  const actions = ManagementActions();

  useEffect(() => {
    // Automatically fetch the API key on component load
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    setLoading(true);
    try {
      const res = await actions?.fetchApiKey();
      if (res?.status === 200 && res?.data && res?.data.length > 0) {
        setResData(res?.data[0] || null);
        setShowKey(false);
        setShowCopyIcon(false)
      } else {
        setResData(null);
      }
    } catch (error) {
      showSnackBar(error?.toString(), 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = async () => {
    setLoading(true);
    try {
      const res = await actions?.generateApiKey();
      if (res?.status === 200 && res?.data?.data?.api_key) {
        setResData({ is_active: true, api_key: res?.data?.data?.api_key });
        setShowKey(true);
        setShowCopyIcon(true);
        showSnackBar("API key is created successfully", 'error');
      } else {
        showSnackBar("API key is not created successfully", 'error');
      }
    } catch (error) {
      showSnackBar(error?.toString(), 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateApiKey = async () => {
    setLoading(true);
    try {
      const res = await actions?.updateApiKey();
      if (res?.status === 200 && res?.data?.data?.new_api_key) {
        setResData({ is_active: true, api_key: res?.data?.data?.new_api_key });
        setShowKey(true);
        setShowCopyIcon(true);
        showSnackBar('API key Regenerated successfully', 'success');
      } else {
        showSnackBar('API key not Regenerated successfully', 'error');
      }
    } catch (error) {
      showSnackBar(error?.toString(), 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (resData?.api_key) {
      navigator.clipboard.writeText(resData?.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleToggleActiveStatus = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    try {
      const isActive: boolean = event.target.checked;
      const res = await actions?.updateApiKey(isActive);
      if (res?.status === 200) {
        setResData(prev => prev ? { ...prev, is_active: isActive, api_key: prev.api_key } : null);
        showSnackBar(`API key is now ${isActive ? 'active' : 'inactive'}`, 'success');
      } else {
        showSnackBar('Failed to update API key status', 'error');
      }
    } catch (error) {
      showSnackBar(error?.toString(), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '32vh', backgroundColor: '#f4f6f8' }}>
      <Card sx={{ maxWidth: 400, padding: 2, boxShadow: 3 }}>
        <CardHeader title="Manage API Key" titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }} />
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Below is your API key. You can regenerate it if needed or copy it securely.
          </Typography>

          {resData?.api_key ? (
            <div style={{ marginBottom: '16px' }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Your API Key
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TextField
                  value={resData?.api_key}
                  type={showKey ? 'text' : 'password'}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  size="small"
                />
                {showCopyIcon &&
                  <>
                    <IconButton onClick={() => setShowKey(!showKey)}>
                      {showKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    <IconButton color="primary" onClick={copyToClipboard}>
                      {copied ? <CheckCircle color="success" /> : <ContentCopy />}
                    </IconButton>
                  </>
                }
              </div>
              {copied && <Typography variant="caption" color="green" sx={{ marginTop: 1 }}>API key copied to clipboard!</Typography>}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
                <Typography variant="body2">Status:</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={resData?.is_active || false}
                      onChange={handleToggleActiveStatus}
                      color="primary"
                    />
                  }
                  label={resData?.is_active ? "Active" : "Inactive"}
                  sx={{ mr: 0 }}
                />
              </Box>
              <LoadingButton
                buttonName={`${loading ? 'Regenerating...' : 'Regenerate API Key'}`}
                isLoading={loading}
                onClickButton={updateApiKey}
                fullWidth={true}
                sx={{ marginTop: 2 }}
              />
            </div>
          ) : (
            <LoadingButton
              buttonName={`${loading ? 'Generating...' : 'Generate API Key'}`}
              isLoading={loading}
              onClickButton={generateApiKey}
              fullWidth={true}
              sx={{ marginTop: 2 }}
            />
          )}
          {showCopyIcon &&
            <Alert severity="warning" sx={{ marginTop: 2 }}>
              <Typography variant="caption">
                For security reasons, you won’t be able to see this key again. Copy it now!
              </Typography>
            </Alert>
          }
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateApiKey;

